import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { TEMPLATE_FORM_FIELD_IDS } from '../constants';
import { CreateTemplateSidebarMode } from '../create-template-sidebar';

interface UseCreateTemplateFormOptions {
  mode?: CreateTemplateSidebarMode;
  initialReportId?: string;
}

export const useCreateTemplateForm = ({ mode = 'report', initialReportId }: UseCreateTemplateFormOptions = {}) => {
  const [templateName, setTemplateName] = useState('');
  const [selectedReportId, setSelectedReportId] = useState(initialReportId || '');
  const isReportPreSelected = !!initialReportId;
  const [errors, setErrors] = useState<Record<string, { id: string; message: string }>>({});

  const intl = useIntl();
  const hasBeenValidated = useRef(false);
  const isDashboard = mode === 'dashboard';

  const validate = useCallback(() => {
    const newErrors: Record<string, { id: string; message: string }> = {};

    // Template name
    if (!templateName.trim().length) {
      newErrors.name = {
        id: TEMPLATE_FORM_FIELD_IDS.TEMPLATE_NAME,
        message: intl.formatMessage({
          id: 'reporting.validation.template_name_is_required',
          defaultMessage: 'Template name is required'
        })
      };
    }

    // Source report/dashboard
    if (!selectedReportId) {
      newErrors.selectedReport = {
        id: TEMPLATE_FORM_FIELD_IDS.REPORT_SELECT,
        message: isDashboard
          ? intl.formatMessage({
              id: 'reporting.validation.you_must_select_a_dashboard',
              defaultMessage: 'You must select a dashboard'
            })
          : intl.formatMessage({
              id: 'reporting.validation.you_must_select_a_report',
              defaultMessage: 'You must select a report'
            })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.keys(newErrors).length === 0;
  }, [templateName, selectedReportId, intl, isDashboard]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  return {
    templateName,
    setTemplateName,
    selectedReportId,
    setSelectedReportId,
    errors,
    validate,
    revalidate,
    isReportPreSelected
  };
};
